import { parseSurfaceGeometry } from '../shared/surface-geometry';
// zone-truth.ts — Lane MV2 S1 (2026-09-28): the client-truth ZONE MOVE / RESIZE adapter + WS subscriber.
//
// The pure resolver (janus-engine/zones/zone-geometry.ts) decides the move against the anchor row the page holds; the row
// is patched in place and re-projected at once (the FT3D zone outline follows); the commit goes to
// options/tools/statuses/ft3d/ft3d_status_move_truth_commit_inc.php (save-only). The browser publishes its saved status delta.
//
// Entry: window.ftTryCommitZoneMove({ encounterId, statusId, span: {ox,oy,oz,ex,ey,ez}, showLogMessage? }).
// It returns 'handled' (committed, refused with a message, or a no-op) or null when the page does not hold the anchor.
import { resolveZoneMove, zoneCenterMovedFt, ZONE_GEOMETRY_SCHEMA, type ZoneSpan, type ZoneTargetKind } from '../janus-engine/zones/zone-geometry.js';
import { fetchAndCacheObject } from './object-cache.js';
import { SHAPES as AOE_SHAPES } from '../janus-engine/ability/aoe/expand-placed-aoe-targets.js';
import { handleCasterUpdate } from './caster-update.js';
import { collectWalkZones, entryDispatches, refreshClassAuraZones, statusCasterId, relocationAuraCoverage, resolveWalkZoneCrossings, type ZoneCoverage, type ZoneEntry, type WalkZoneReaders, type WalkZoneResolution, type ZoneStrip } from '../janus-engine/zones/walk-zone-crossings.js';
import { collectPersistentZones, intersectingZoneKeys, zoneContains, zoneIsPainted } from '../janus-engine/zones/zone-membership.js';
import { mayBeSwarm, type AoeOccupancy } from '../janus-engine/ability/aoe/expand-placed-aoe-targets.js';
import { position as perceptionPosition, hasStatus, sightRayZ } from '../encounter-map/ft3d/perception.js';
import { validateShape } from '../../../ft3d/engine/ft3d-shape-core.js';
import { drawnCreatureVolume } from '../shared/physical-creature-body.js';
import { combatSizeFt, type AbilityPreconditionReaders } from '../janus-engine/actions/ability-preconditions.js';
import type { CasterArr } from '../shared/array-types.js';
import { planEndOfTurnZones, type ZoneEndDecision, type ZoneEndReaders } from '../janus-engine/zones/end-of-turn-zones.js';
import type { JanusReaders } from '../janus-engine/ability/context.js';
import { statusPaused } from '../janus-engine/statuses/expiry.js';
import { fullDefinition } from '../janus-engine/reactions/php-row-rules.js';
import { WALL_GASEOUS_FORM_ID, WALL_GASEOUS_STATUS_IDS } from '../shared/painted-wall-rules.js';
import { withAsyncDeadline } from '../shared/async-deadline';
import type { AppliedFrame } from '../janus-engine/transport/applied-frame';

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const rows = (value: unknown): Row[] => Array.isArray(value) ? value.filter(v => asRow(v) !== null) as Row[] : [];
const ENDPOINT = '/options/tools/statuses/ft3d/ft3d_status_move_truth_commit_inc.php';
const GEOMETRY_KEYS = ['status_origin_point_x', 'status_origin_point_y', 'status_origin_point_z',
  'status_distance_point_x', 'status_distance_point_y', 'status_distance_point_z', 'status_aoe_distance'] as const;
type SavedSpan = ZoneSpan & { distance: number };

function encounterFor(id: string): Row | null {
  const cache = window.FateTide?.encounterCache;
  return asRow(cache?.get?.(id) ?? cache?.get?.(upper(id)));
}
const num = (value: unknown): number => { const n = Number(Array.isArray(value) ? value[0] : value); return Number.isFinite(n) ? n : 0; };
const spanOf = (entry: Row): SavedSpan => ({
  ox: num(entry.status_origin_point_x), oy: num(entry.status_origin_point_y), oz: num(entry.status_origin_point_z),
  ex: num(entry.status_distance_point_x), ey: num(entry.status_distance_point_y), ez: num(entry.status_distance_point_z),
  distance: num(entry.status_aoe_distance),
});
const entityId = (row: Row): string => upper(asRow(row.encounter_creatures)?.encounter_creature_id ?? asRow(row.inventory)?.inventory_id);

interface Located { entry: Row; kind: ZoneTargetKind; holder: Row | null; casterRow: Row | null }
/** The page's existing anchor reader, including encounter-, creature- and inventory-held zones. */
export function zoneAnchorRow(encounter: Row, statusId: string): Row | null { return locate(encounter, statusId)?.entry ?? null; }
/** The anchor row as this page holds it: on the encounter's own statuses, a creature row or an inventory row. */
function locate(encounter: Row, statusId: string): Located | null {
  const sid = upper(statusId);
  const find = (row: Row, mode: 'target' | 'caster'): Row | null =>
    rows(asRow(row.statuses)?.[mode]).find(e => upper(e.status_id) === sid) ?? null;
  const own = find(encounter, 'target');
  let located: Located | null = own ? { entry: own, kind: 'encounter', holder: null, casterRow: null } : null;
  if (!located) for (const row of rows(encounter.encounter_creatures_arr)) {
    const e = find(row, 'target');
    if (e && upper(e.status_target_id) === entityId(row)) { located = { entry: e, kind: 'encounter_creature', holder: row, casterRow: null }; break; }
  }
  if (!located) for (const row of rows(encounter.encounter_inventories_arr)) {
    const e = find(row, 'target');
    if (e) { located = { entry: e, kind: 'inventory', holder: row, casterRow: null }; break; }
  }
  if (!located) return null;
  const casterId = upper(located.entry.status_caster_id);
  located.casterRow = rows(encounter.encounter_creatures_arr).find(r => entityId(r) === casterId && r !== located!.holder) ?? null;
  return located;
}

type ListOrder = { target?: string[]; caster?: string[] };
function writeSpan(row: Row, statusId: string, span: SavedSpan, applyY: boolean, order?: ListOrder): boolean {
  const sid = upper(statusId);
  const values: Record<string, number> = { status_origin_point_x: span.ox, status_origin_point_z: span.oz,
    status_distance_point_x: span.ex, status_distance_point_z: span.ez, status_aoe_distance: span.distance };
  if (applyY) { values.status_origin_point_y = span.oy; values.status_distance_point_y = span.ey; }
  let touched = false;
  const statuses = asRow(row.statuses);
  if (!statuses) return false;
  for (const mode of ['target', 'caster'] as const) {
    const list = Array.isArray(statuses[mode]) ? statuses[mode] as unknown[] : null;
    if (!list) continue;
    const i = list.findIndex(e => upper(asRow(e)?.status_id) === sid);
    if (i < 0) continue;
    const entry = { ...(asRow(list[i]) ?? {}) };
    for (const key of GEOMETRY_KEYS) {
      if (!(key in values)) continue;
      const v = values[key]!;
      if (v === 0) delete entry[key]; else entry[key] = v;
    }
    list[i] = entry;
    const rank = order?.[mode];
    if (Array.isArray(rank)) {
      // The server's SQL rank (status_t_updated DESC, status_id DESC) — the same stable re-sort the cache patch applies.
      const at = new Map(rank.map((id, n) => [upper(id), n]));
      const keyed = list.map((e, n) => ({ e, n, r: at.get(upper(asRow(e)?.status_id)) ?? Number.MAX_SAFE_INTEGER }));
      keyed.sort((a, b) => a.r - b.r || a.n - b.n);
      list.splice(0, list.length, ...keyed.map(k => k.e));
    } else if (i > 0) {
      // Optimistic (no rank yet): the rewritten row is the newest-updated, so it leads.
      list.splice(i, 1);
      list.unshift(entry);
    }
    touched = true;
  }
  return touched;
}

/** Patch every held copy (the encounter blob and the FT3D projection cache when it is a separate object), then re-project. */
function applySpan(encounterId: string, statusId: string, span: SavedSpan, applyY: boolean, orders?: Record<string, ListOrder>): boolean {
  const encounter = encounterFor(encounterId);
  if (!encounter) return false;
  const roots = [encounter];
  const ft3d = asRow((window as unknown as { ft3dEncounterArrCache?: { encounterId?: string; arr?: unknown } }).ft3dEncounterArrCache);
  const ft3dArr = ft3d && upper(ft3d.encounterId) === upper(encounterId) ? asRow(ft3d.arr) : null;
  if (ft3dArr && ft3dArr !== encounter) roots.push(ft3dArr);
  const orderFor = (id: string): ListOrder | undefined => orders?.[upper(id)] ?? orders?.[id];
  let any = false;
  const renderRows: Row[] = [];
  for (const root of roots) {
    const located = locate(root, statusId);
    if (!located) continue;
    if (located.kind === 'encounter') any = writeSpan(root, statusId, span, applyY, orderFor(encounterId)) || any;
    if (located.holder) {
      any = writeSpan(located.holder, statusId, span, applyY, orderFor(entityId(located.holder))) || any;
      if (root === encounter && located.kind === 'encounter_creature') renderRows.push(located.holder);
    }
    if (located.casterRow) {
      writeSpan(located.casterRow, statusId, span, applyY, orderFor(entityId(located.casterRow)));
      if (root === encounter) renderRows.push(located.casterRow);
    }
  }
  for (const row of [...new Set(renderRows)]) {
    try { handleCasterUpdate({ type: 'caster_update', caster_arr: row as unknown as CasterArr, local: true }, ['statuses']); } catch { /* non-fatal */ }
  }
  // The encounter's own zones (ground placements) are projected from the encounter blob: re-announce it, the same
  // way the inventory-move receipt does (ft3d-move-handlers) — the FT3D child redraws the zone outline from it.
  window.dispatchEvent(new CustomEvent('ft:encounter-state-received', {
    detail: { encounterId: upper(encounterId), encounterArr: encounter, subType: 'janus_zone_truth', inventoryUnchanged: true,
      changedCreatureIds: renderRows.map(entityId) },
  }));
  return any;
}

// ONE reconciliation path for WS frames and HTTP answers (Codex S1 review F4). Each status remembers the newest server
// revision it painted; an older revision never replaces a newer one, whatever order frames / answers arrive in. While an own
// move is still in flight, saved truth for an EARLIER operation is banked but not painted — the newer own move's answer lands.
const known = new Map<string, { rev: number; final: boolean; span: SavedSpan; applyY: boolean; orders?: Record<string, ListOrder> }>();
const pendingOwn = new Map<string, string>(); // status id → the newest own operation still awaiting its answer
const ownOps = new Set<string>();
function reconcile(encounterId: string, sid: string, op: string, rev: number, final: boolean, span: SavedSpan, applyY: boolean, orders?: Record<string, ListOrder>): boolean {
  const prior = known.get(sid);
  if (prior && (rev < prior.rev || (rev === prior.rev && (prior.final || !final)))) return false;
  if (!final && ownOps.has(op)) return false; // our own optimistic apply already shows it
  known.set(sid, { rev, final, span, applyY, orders });
  if (known.size > 256) known.delete(known.keys().next().value!);
  const mine = pendingOwn.get(sid);
  if (final && mine && mine !== op) return false; // banked: a newer own move is in flight
  applySpan(encounterId, sid, span, applyY, orders);
  return true;
}

export interface ZoneTruthFrame { schema: string; operation_id: string; encounter_id: string; status_id: string; rev?: number;
  provisional?: boolean; committed?: boolean; correction?: boolean; frame?: { span?: SavedSpan; apply_y?: boolean; order?: Record<string, ListOrder> } }
const validSpan = (span: unknown): span is SavedSpan => {
  const s = asRow(span);
  return !!s && ['ox', 'oy', 'oz', 'ex', 'ey', 'ez', 'distance'].every(k => Number.isFinite(Number(s[k])));
};
/** Check before the shared saved-row installer, including the initiating transport's local install. */
export function zoneTruthMayInstall(value: unknown): boolean {
  const command = asRow(value);
  if (!command || command.schema !== ZONE_GEOMETRY_SCHEMA) return false;
  const sid = upper(command.status_id), prior = known.get(sid), rev = Number(command.rev);
  if (!Number.isFinite(rev) || prior && (rev < prior.rev || rev === prior.rev && prior.final)) return false;
  return true;
}
/** Inbound `janus_zone_truth` (router.ts). */
export function handleZoneTruth(command: ZoneTruthFrame): void {
  if (!command || command.schema !== ZONE_GEOMETRY_SCHEMA || !validSpan(command.frame?.span) || !Number.isFinite(Number(command.rev))) return;
  const final = command.committed === true || command.correction === true;
  const painted = reconcile(command.encounter_id, upper(command.status_id), upper(command.operation_id), Number(command.rev), final,
    command.frame!.span!, command.frame?.apply_y !== false, command.frame?.order);
  window.dispatchEvent(new CustomEvent('ft:zone-truth-frame', { detail: { command, painted } }));
}

export interface ZoneMoveRequest { encounterId: string; statusId: string; span: ZoneSpan; showLogMessage?: boolean }
export type ZoneMoveOutcome = 'handled' | null;
const warn = (message: string): void => { if (typeof displayWarning === 'function') displayWarning(message); };

export async function tryCommitZoneMove(input: ZoneMoveRequest): Promise<ZoneMoveOutcome> {
  const encounterId = upper(input.encounterId), statusId = upper(input.statusId);
  const encounter = encounterFor(encounterId);
  if (!encounter) return null;
  const located = locate(encounter, statusId);
  if (!located || Number(located.entry.status_aoe_source ?? 0) !== 1) return null;
  if (located.entry.status_aoe_geometry != null && located.entry.status_aoe_geometry !== '' && !parseSurfaceGeometry(located.entry.status_aoe_geometry)) { warn('A painted wall cannot be dragged; recast it instead.'); return 'handled'; }
  const aoeTypeId = upper(located.entry.status_aoe_type) || null;
  const aoeVar = aoeTypeId ? (AOE_SHAPES[aoeTypeId] ?? '') : '';
  if (aoeTypeId && !aoeVar) return null; // a shape outside the seven the engine knows: its legacy radius rule is not held here
  const catalog = asRow(encounter.catalog);
  const before = spanOf(located.entry);
  const itemId = upper(located.entry.status_item_id);
  let def = asRow(catalog?.[itemId]) ?? asRow(window.FateTide?.objectCache?.get(itemId));
  if (!def && located.kind !== 'encounter' && zoneCenterMovedFt(before, input.span, true) > 0) {
    try { def = asRow(await withAsyncDeadline(() => fetchAndCacheObject(itemId), 6000)); } catch { /* named refusal below */ }
    if (!def) { warn('zone-move:definition-not-held'); return 'handled'; }
  }
  const follow = def ? ftLayerValue(def, 'aoe_follows_target') : null;
  const verdict = resolveZoneMove({ before, after: input.span, applyY: true, aoeTypeId, aoeVar,
    followsTarget: follow !== null && follow !== undefined && follow !== '' && follow !== 0 && follow !== '0', targetKind: located.kind });
  if (verdict.kind === 'unchanged') return 'handled';
  if (verdict.kind === 'refused') { warn(verdict.message); return 'handled'; }
  if (!window.ftPushAppliedTruth) { warn('zone-move:truth-transport-unavailable'); return 'handled'; }
  const after: SavedSpan = { ...input.span, distance: verdict.aoeDistance };
  const operationId = crypto.randomUUID().toUpperCase();
  ownOps.add(operationId);
  if (ownOps.size > 128) ownOps.delete(ownOps.values().next().value!);
  pendingOwn.set(statusId, operationId);
  const startRev = known.get(statusId)?.rev ?? 0;
  applySpan(encounterId, statusId, after, true);
  const settle = (): void => { if (pendingOwn.get(statusId) === operationId) pendingOwn.delete(statusId); };
  const body = { schema: ZONE_GEOMETRY_SCHEMA, operation_id: operationId, encounter_id: encounterId, status_id: statusId,
    before, after, apply_y: true, ft_moved: verdict.ftMoved, show_log_message: input.showLogMessage === false ? 0 : 1 };
  try {
    for (let attempt = 0; attempt < 3; attempt++) {
      let response: Response, result: Row | null;
      try {
        ({ response, result } = await withAsyncDeadline(async signal => {
          const response = await fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal });
          return { response, result: asRow(await response.clone().json().catch(() => null)) };
        }, 6000));
      } catch { continue; } // no answer: the same operation id retries idempotently
      if (response.ok && result?.ok === true) {
        const saved = asRow(result.frame)?.span;
        settle();
        if (validSpan(saved) && Number.isFinite(Number(result.rev))) {
          const order = asRow(asRow(result.frame)?.order) as Record<string, ListOrder> | null;
          const fields = Object.fromEntries(GEOMETRY_KEYS.map((key, i) => [key, saved[(['ox', 'oy', 'oz', 'ex', 'ey', 'ez', 'distance'] as const)[i]!]]));
          const applied: AppliedFrame = { kind: 'applied', operation_id: operationId, encounter_id: encounterId, actor_id: encounterId,
            rev: 0, member_revs: {}, computed: {}, new_entities: [], deletes: [],
            rows: [{ table: 'statuses', row_id: statusId, partial: true, row: { status_id: statusId, ...fields } }] };
          const command = { ...body, actor_id: encounterId, rev: Number(result.rev), committed: true,
            frame: { span: saved, apply_y: true, ...(order ? { order } : {}) } };
          // Register delivery before acknowledging the completed save, retaining the exact bytes across reconnect.
          await withAsyncDeadline(() => Promise.resolve(window.ftPushAppliedTruth!({ type: 'janus_zone_truth', command, applied_frame: applied })), 6000);
          await window.ftSaveAppliedTruth?.(operationId, async () => response);
          if (!reconcile(encounterId, statusId, operationId, Number(result.rev), true, saved, true, order ?? undefined)
            && !pendingOwn.has(statusId)) {
            const latest = known.get(statusId);
            if (latest) applySpan(encounterId, statusId, latest.span, latest.applyY, latest.orders);
          }
        }
        return 'handled';
      }
      if (response.status >= 500 && attempt < 2) continue;
      settle();
      if (response.status === 409 && validSpan(result?.span) && Number.isFinite(Number(result?.rev))) {
        reconcile(encounterId, statusId, operationId, Number(result!.rev), true, result!.span as SavedSpan, true);
      } else if ((known.get(statusId)?.rev ?? 0) <= startRev && !pendingOwn.has(statusId)) {
        applySpan(encounterId, statusId, before, true); // refused with nothing newer painted since: put the zone back
      }
      const message = String(asRow(result)?.message ?? result?.error ?? response.status);
      warn(response.status === 409 ? 'The zone changed elsewhere; its current position was restored.'
        : response.status === 403 ? 'Only the campaign GM can move this zone.' : 'The zone move was refused: ' + message);
      return 'handled';
    }
    warn('zone-move:save-unconfirmed — the zone move could not be confirmed.');
    return 'handled';
  } finally {
    settle();
  }
}

Object.assign(window, { ftTryCommitZoneMove: tryCommitZoneMove, ftApplyZoneTruthFrame: handleZoneTruth, ftZoneTruthMayInstall: zoneTruthMayInstall });

// ── S3: the end-of-move zone EXIT sweep ─────────────────────────────────────────────────────────────────────────────────
// resolveWalkZoneCrossings (janus-engine/zones/walk-zone-crossings.ts) decides, for one flat walk, whether the legacy zone
// sweeps would do nothing, strip exact rows, or name an unsupported classifier case. The entry adapter registers the shared
// ability persistence here, avoiding a stateful import cycle through movement-truth.
function sceneShape(encounter: Row): Row | null {
  let raw: unknown = asRow(encounter.encounters)?.encounter_shape ?? encounter.encounter_shape;
  if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { return null; } }
  const shape = asRow(raw);
  if (!shape) return null;
  try { validateShape(shape); } catch { return null; }
  return shape;
}
const layerHas = (row: Row | null, key: string): boolean => !!row && (ftLayerValue(row, key) != null || (ftLayerEntries(row, key) ?? []).length > 0);
function movedTo(row: Row, x: number, y: number): Row {
  const position = { ...(asRow(row.position) ?? {}), x_pos: x, y_pos: y };
  return { ...row, position, encounter_creatures: { ...(asRow(row.encounter_creatures) ?? {}), x_pos: x, y_pos: y } };
}

function walkReaders(encounter: Row, shape: Row | null): WalkZoneReaders {
  const catalog = asRow(encounter.catalog);
  const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
  return {
    movementProfile: row => {
      const sizeId = upper(scalar(ftLayerValue(row, 'size')));
      if (!sizeId) return undefined;
      return { sizeId, gaseous: upper(ftEncounterCreatureField(asRow(row.encounter_creatures), 'item_id')) === WALL_GASEOUS_FORM_ID
        || upper(ftItemsCol(row, 'item_id')) === WALL_GASEOUS_FORM_ID
        || ftCreatureStatuses(row, 'target').some(status => !statusPaused(status)
          && WALL_GASEOUS_STATUS_IDS.includes(upper(scalar(status.status_item_id) ?? ftItemsCol(status, 'item_id')))) };
    },
    position: row => { const at = perceptionPosition(row); return [at.x, at.y ?? 0, at.z]; },
    occupancy: row => asRow(row.encounter_creatures) ? drawnCreatureVolume(row, shape,
      { catalog: encounter.catalog, clipCeiling: false, position: perceptionPosition, hasStatus }) : null,
    rayZ: (row, x, y) => shape ? sightRayZ(shape as never, movedTo(row, x, y)) : undefined,
    definition: id => asRow(catalog?.[id] ?? catalog?.[id.toUpperCase()]),
    has: (definition, layer) => layerHas(definition, layer),
    layer: (row, key) => ftLayerValue(row, key),
    // ft_combat_range_object_size_ft(): the size-tier width (a swarm's widened frontage is not ported → unproved).
    // Two reader shapes PHP's get_object_size_id_for_footprint() answers differently are unproved too (Codex class-aura review
    // F4): a flat root-level inventory_size (inventory_field() reads the bucket only) and a non-canonical size id
    // (get_size_dimensions() matches case-sensitively; the browser uppercases).
    combatSizeFt: row => {
      if (mayBeSwarm(row, layerHas) || 'inventory_size' in row) return undefined;
      const readers = { value: (t: Row | null, k: string) => t ? ftLayerValue(t, k) : null,
        inventoryField: (t: Row | null, k: string) => t ? ftInventoryField(t, k) : null } as unknown as AbilityPreconditionReaders;
      const override = ftInventoryField(row, 'inventory_size');
      const ids = [override, ftLayerValue(row, 'size'), ftLayerValue(asRow(row.encounter_creatures) ?? {}, 'size')]
        .map(v => Array.isArray(v) ? v[0] : v).filter(v => typeof v === 'string' && v !== '') as string[];
      if (ids.some(v => v !== v.trim().toUpperCase())) return undefined;
      return combatSizeFt(row, readers);
    },
  };
}
export function resolveWalkZones(encounterId: string, moverId: string, route: ReadonlyArray<readonly number[]>,
  options: { entries?: boolean; moved?: ReadonlyMap<string, readonly [number, number]>; coverage?: boolean; teleport?: boolean; flying?: boolean } = {}): WalkZoneResolution {
  let encounter = encounterFor(encounterId);
  if (!encounter) return { kind: 'handback', reason: 'persistent-zone:encounter-not-held' };
  // Janus session 3 (additive, 2026-09-29): a compound walk's FOLLOWERS stand at their landings for the end-of-move sweeps (they never
  // enter a zone — final_creature_move moves them with set_pos_coord); every other reader sees the page's rows unchanged.
  if (options.moved?.size) {
    const moved = options.moved;
    encounter = { ...encounter, encounter_creatures_arr: rows(encounter.encounter_creatures_arr).map(r => {
      const to = moved.get(entityId(r));
      return to && entityId(r) !== upper(moverId) ? movedTo(r, to[0], to[1]) : r;
    }) };
  }
  const mover = rows(encounter.encounter_creatures_arr).find(r => entityId(r) === upper(moverId));
  if (!mover) return { kind: 'handback', reason: 'persistent-zone:mover-not-held' };
  const shape = sceneShape(encounter);
  const followers = new Set([...(options.moved?.keys() ?? [])].map(upper).filter(id => id !== upper(moverId)));
  return resolveWalkZoneCrossings({ encounter, mover, route, shape, read: walkReaders(encounter, shape), entries: options.entries === true, coverage: options.coverage, teleport: options.teleport, flying: options.flying,
    ...(followers.size ? { followers } : {}) });
}

/** The broad phase must use the same current radius as exact membership. Refresh only this carrier/anchor, borrowing held rows. */
export function walkZoneRadius(encounterId: string, holder: Row, status: Row): number | undefined {
  const encounter = encounterFor(encounterId);
  if (!encounter) return undefined;
  const read = walkReaders(encounter, null);
  try {
    const [zone] = refreshClassAuraZones([{ status, key: upper(status.status_id), holderId: entityId(holder), dynamic: read.position(holder) }], [holder], read);
    return num(zone!.status.status_aoe_distance);
  } catch { return undefined; } // Unknown bounds must reach the exact classifier's named diagnostic.
}

/** Administrative teleports compare the endpoints; they never enter a zone along the imaginary segment. */
export function planGmRelocationZones(before: Row, after: Row, moverId: string, transfer = false, definition?: WalkZoneReaders['definition']):
  { kind: 'resolved'; entries: ZoneEntry[]; covered: ZoneCoverage[]; strips: Array<ZoneStrip & { itemId: string }> } | { kind: 'handback'; reason: string } {
  try {
    const creatures = rows(after.encounter_creatures_arr), mover = creatures.find(r => entityId(r) === upper(moverId));
    const previous = rows(before.encounter_creatures_arr).find(r => entityId(r) === upper(moverId));
    if (!mover || (!transfer && !previous)) return { kind: 'handback', reason: 'persistent-zone:mover-not-held' };
    const shape = sceneShape(after), read = { ...walkReaders(after, shape), ...(definition ? { definition } : {}) }, zones = collectWalkZones(after, read);
    const oldRead = { ...walkReaders(before, sceneShape(before)), ...(definition ? { definition } : {}) };
    const oldKeys = new Set(transfer ? [] : intersectingZoneKeys(previous!, collectWalkZones(before, oldRead), sceneShape(before), oldRead));
    const newKeys = new Set(intersectingZoneKeys(mover, zones, shape, read));
    const entries: ZoneEntry[] = [];
    for (const zone of zones) {
      if (!newKeys.has(zone.key) || oldKeys.has(zone.key) || !entryDispatches(zone, mover, creatures, read)) continue;
      entries.push({ zoneStatusId: upper(zone.status.status_id), connectedId: upper(zone.status.status_connected_id),
        originId: upper(zone.status.status_origin_id || zone.status.status_item_id), casterId: statusCasterId(zone.status),
        step: 0, point: read.position(mover).slice(0, 2) as [number, number], forcedExtreme: Math.sign(Number(zone.status.status_forced_roll_extreme ?? 0)) });
    }
    const at = read.position(mover);
    const exit = resolveWalkZoneCrossings({ encounter: after, mover, route: [[at[0], at[1]], [at[0], at[1]]], shape, read });
    if (exit.kind === 'handback') return exit;
    const strips = exit.kind === 'exits' || exit.kind === 'entries' ? exit.strips : [];
    const statuses = creatures.flatMap(r => ftCreatureStatuses(r, 'target') ?? []);
    const covered = relocationAuraCoverage(transfer ? { encounter_creatures_arr: [] } : before, after, moverId, shape, read, sceneShape(before), oldRead);
    return { kind: 'resolved', entries, covered, strips: strips.map(strip => {
      const status = statuses.find(s => upper(s.status_id) === upper(strip.statusId));
      if (!status) throw new Error('persistent-zone:exit-row-not-held');
      return { ...strip, itemId: upper(status.status_item_id ?? asRow(asRow(status.status_arr)?.items)?.item_id) };
    }) };
  } catch (error) { return { kind: 'handback', reason: error instanceof Error ? error.message : String(error) }; }
}
/** A GM relocation that newly enters, exits and covers no zone leaves the boundary nothing to resolve (rebuild audit 2026-10-26: Move Party
 *  spent 9–25 s warming ~50 definitions to reach that empty verdict). Judged on definitions the page holds IN FULL: a missing one already
 *  hands back, and a partial one could hide a class aura, so either answers false and the caller resolves in full. */
export function relocationTouchesNoZone(before: Row, after: Row, movedIds: readonly string[], transfer: boolean,
  definition: WalkZoneReaders['definition']): boolean {
  const held = (id: string): Row | null => {
    const def = definition(id);
    if (def && !fullDefinition(def, 'any')) throw new Error('gm-relocation:definition-held-in-part');
    return def;
  };
  return movedIds.every(id => {
    const plan = planGmRelocationZones(before, after, id, transfer, held);
    return plan.kind === 'resolved' && !plan.entries.length && !plan.strips.length && !plan.covered.length;
  });
}
/** Janus session 3 step 7 (additive, 2026-09-29): a FORCE MOVE's zone work. The legacy force branch runs no per-foot loop (no entry) and
 *  ends in reconcile_persistent_aoe_exits_for_encounter — the level-triggered sweep alone. The mover is presented at its landing (all three
 *  axes) and judged on a zero-length route, so the classifier runs exactly its end-of-move sweep (movement_force_zone_oracle.php). */
export function resolveForceMoveZones(encounterId: string, moverId: string, at: { x: number; y: number; z: number },
  /** Codex step-7 F1: the RUNTIME shape (ft_encounter_shape — live doors / breaches, as the scene renders it); `overlayUnknown` = the page
   *  could not apply them, so any zone hands back (a closed door between a creature and a zone's centre changes membership). */
  world?: { shape: unknown; overlayUnknown: boolean },
  /** step 7 compounds: the followers stand at their landings too (the reconcile runs after final_creature_move moved them). */
  moved?: ReadonlyMap<string, { x: number; y: number; z: number }>,
  /** Session A2 0b: ft_spatial_row_volume for a painted wall's hazard test — the caller's (force-move-truth.ts: ability-truth's placed
   *  readers). Injected, never imported: zone-truth → ability-truth → movement-truth → zone-truth is an init cycle. */
  spatialVolume?: (encounter: Row) => (row: Row) => AoeOccupancy | undefined): WalkZoneResolution {
  let encounter = encounterFor(encounterId);
  if (!encounter) return { kind: 'handback', reason: 'persistent-zone:encounter-not-held' };
  encounter = { ...encounter, encounter_creatures_arr: rows(encounter.encounter_creatures_arr).map(r => {
    const p = entityId(r) === upper(moverId) ? at : moved?.get(entityId(r));
    if (!p) return r;
    const position = { ...(asRow(r.position) ?? {}), x_pos: p.x, y_pos: p.y, z_pos: p.z };
    return { ...r, position, encounter_creatures: { ...(asRow(r.encounter_creatures) ?? {}), x_pos: p.x, y_pos: p.y, z_pos: p.z } };
  }) };
  const mover = rows(encounter.encounter_creatures_arr).find(r => entityId(r) === upper(moverId));
  if (!mover) return { kind: 'handback', reason: 'persistent-zone:mover-not-held' };
  const shape = world ? asRow(world.shape) : sceneShape(encounter);
  if (world?.overlayUnknown) {
    try { if (collectPersistentZones(encounter, walkReaders(encounter, shape)).length > 0) return { kind: 'handback', reason: 'persistent-zone:overlay-unknown' }; }
    catch { return { kind: 'handback', reason: 'persistent-zone:overlay-unknown' }; }
  }
  // Session A2 0b (2026-09-30): the force move's reconcile judges a PAINTED wall too (the deleted legacy branch did — a GM forcing a creature out of
  // Wall of Flesh / Prismatic Wall): ft_painted_wall_target_intersection against the row's ft_spatial_row_volume (the placed readers' twin).
  const read: WalkZoneReaders = { ...walkReaders(encounter, shape), ...(spatialVolume ? { spatialVolume: spatialVolume(encounter) } : {}) };
  const verdict = resolveWalkZoneCrossings({ encounter, mover, route: [[at.x, at.y], [at.x, at.y]], shape, read });
  return verdict.kind === 'entries' ? { kind: 'handback', reason: 'persistent-zone:force-entry' } : verdict;
}
/** END hooks: exact exit strips before target ticks, then intersecting zone dispatches.
 * Unsupported geometry/lifecycle tails retain named pre-draw hand-backs. */
export function endOfTurnZones(encounterId: string, creatureId: string, ability: JanusReaders, alreadyResolved?: ZoneEndReaders['alreadyResolved']): ZoneEndDecision {
  const encounter = encounterFor(encounterId);
  if (!encounter) return { kind: 'handback', reason: 'encounter-not-held' };
  return endOfTurnZonesFor(encounter, creatureId, ability, alreadyResolved);
}
export function endOfTurnZonesFor(encounter: Row, creatureId: string, ability: JanusReaders, alreadyResolved?: ZoneEndReaders['alreadyResolved']): ZoneEndDecision {
  const creature = rows(encounter.encounter_creatures_arr).find(r => entityId(r) === upper(creatureId));
  if (!creature) return { kind: 'handback', reason: 'creature-not-held' };
  const shape = sceneShape(encounter);
  const read = walkReaders(encounter, shape);
  return planEndOfTurnZones(encounter, creature, shape, { ...read, ability, alreadyResolved });
}
/** Does this creature — where the page holds it — stand in this zone (the legacy predicate, wall gate included)? null = not judgeable. The
 *  zone-entry adapter uses it for a zone's CASTER after an entry applied it rows: the end-of-move sweep strips them when it stands outside. */
export function creatureStandsInZone(encounterId: string, creatureId: string, zoneStatusId: string): boolean | null {
  const encounter = encounterFor(encounterId);
  if (!encounter) return null;
  const creature = rows(encounter.encounter_creatures_arr).find(r => entityId(r) === upper(creatureId));
  if (!creature) return null;
  const shape = sceneShape(encounter);
  const read = walkReaders(encounter, shape);
  try {
    const zones = collectWalkZones(encounter, read, true);
    const zone = zones.find(z => upper(z.status.status_id) === upper(zoneStatusId));
    if (!zone) return null;
    return intersectingZoneKeys(creature, zones, shape, read).includes(zone.key);
  } catch { return null; }
}
export type ZoneExitOutcome = { kind: 'committed'; stripped: string[] } | { kind: 'refused'; status: number; reason: string } | { kind: 'unknown'; error: string };
let zoneExitCommitter: ((encounterId: string, strips: ZoneStrip[]) => Promise<ZoneExitOutcome>) | null = null;
export function registerZoneExitCommitter(commit: NonNullable<typeof zoneExitCommitter>): void { zoneExitCommitter = commit; }
/** Commit a walk/reconcile's browser-resolved exits through the ability outbox. */
export async function commitZoneExits(encounterId: string, strips: ZoneStrip[]): Promise<ZoneExitOutcome> {
  if (!strips.length) return { kind: 'committed', stripped: [] };
  return zoneExitCommitter ? zoneExitCommitter(encounterId, strips) : { kind: 'unknown', error: 'zone-exit:adapter-not-ready' };
}
/** The level-triggered reconcile for the encounter as the page holds it NOW (a zero-length "walk" by `moverId`). */
export async function reconcileZonesNow(encounterId: string, moverId: string): Promise<WalkZoneResolution | ZoneExitOutcome> {
  const encounter = encounterFor(encounterId);
  const mover = encounter ? rows(encounter.encounter_creatures_arr).find(r => entityId(r) === upper(moverId)) : null;
  if (!mover) return { kind: 'handback', reason: 'persistent-zone:mover-not-held' };
  const at = perceptionPosition(mover);
  const resolution = resolveWalkZones(encounterId, moverId, [[at.x, at.y ?? 0], [at.x, at.y ?? 0]]);
  if (resolution.kind !== 'exits') return resolution;
  return commitZoneExits(encounterId, resolution.strips);
}
Object.assign(window, { ftZoneWalkResolve: resolveWalkZones, ftCommitZoneExits: commitZoneExits, ftZoneReconcileNow: reconcileZonesNow });

// ── S1b: the FT3D GM zone edit ──────────────────────────────────────────────────────────────────────────────────────────
// The scene posts `ft3d-campaign-zone-edit-request-v1` {statusId} on a double-click on a zone's volume; the parent opens the
// placement tool in `zone-edit` mode at the zone's stored span (encounter-map FT3DAOEPlacement.startZoneEdit), and its
// Confirm commits through tryCommitZoneMove above. Only a campaign GM page opens it (the commit re-judges from the row).
export function openZoneEdit(encounterId: string, statusId: string): boolean {
  const encounter = encounterFor(encounterId);
  const located = encounter ? locate(encounter, statusId) : null;
  if (!encounter || !located || typeof window.ftStartZoneEdit !== 'function') return false;
  const entry = located.entry;
  const shape = AOE_SHAPES[upper(entry.status_aoe_type)];
  const span = spanOf(entry);
  if (!shape || !(span.distance > 0)) return false;
  const def = asRow(asRow(encounter.catalog)?.[upper(entry.status_item_id)]);
  const name = String(asRow(def?.items)?.item_name ?? def?.item_name ?? 'Zone');
  const origin = { x: span.ox, y: span.oy, z: span.oz }, endpoint = { x: span.ex, y: span.ey, z: span.ez };
  return window.ftStartZoneEdit({ encounterId: upper(encounterId), statusId: upper(statusId), name, shape: shape as never,
    // A line / cone is edited at its DRAWN length (the stored radius of a line measures x / z only — BUG LEDGER MV2 2026-09-28);
    // the commit re-derives the stored radius from the confirmed span, which repairs it.
    distanceFt: shape === 'line' || shape === 'cone' ? Math.max(1, Math.hypot(span.ex - span.ox, span.ey - span.oy, span.ez - span.oz)) : span.distance,
    heightFt: Math.max(1, num(entry.status_aoe_distance_height)), depthFt: Math.max(1, num(entry.status_aoe_distance_depth)), origin, endpoint });
}
window.addEventListener('message', event => {
  if (event.origin !== window.location.origin) return;
  const data = asRow(event.data);
  if (data?.type !== 'ft3d-campaign-zone-edit-request-v1') return;
  if (window.FateTide?.user?.isGM !== true) return; // the page's campaign-GM flag (persistent-zones.ts reads the same)
  const statusId = upper(asRow(data.payload)?.statusId);
  const encounterId = upper(window.ftGetActiveEncounterId?.());
  if (!statusId || !encounterId) return;
  openZoneEdit(encounterId, statusId);
});
Object.assign(window, { ftOpenZoneEdit: openZoneEdit });
declare global {
  interface Window {
    ftZoneTruthMayInstall?: typeof zoneTruthMayInstall;
    /** encounter-map AOEManager (S1b): opens the placement tool in zone-edit mode. */
    ftStartZoneEdit?: (request: { encounterId: string; statusId: string; name: string; shape: never; distanceFt: number; heightFt: number; depthFt: number;
      origin: { x: number; y: number; z: number }; endpoint: { x: number; y: number; z: number } }) => boolean;
  }
}
